import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { ApiUrl } from "../../constants";
import ProjetApi from "../launchCampagn/projet.api";
import { prepareHeaders } from "../user/user.api";
import { Bareme, RegleFormData, RegleRetrait } from "./regle.type";
import {
  Retrait,
  RetraitFormData,
  RetraitStatutFormData,
} from "./retrait.type";

const RetraitApi = createApi({
  reducerPath: "retrait",
  baseQuery: fetchBaseQuery({
    baseUrl: `${ApiUrl}/api`,
    prepareHeaders,
  }),
  tagTypes: ["retrait", "regle"],
  endpoints: (build) => ({
    // Le backend restreint deja la liste : un porteur ne recoit que les
    // retraits de ses projets, un admin les recoit tous.
    retraits: build.query<Retrait[], { projet?: number; statut?: string }>({
      query: ({ projet, statut }) => {
        const params = new URLSearchParams();
        if (projet) params.append("projet", String(projet));
        if (statut) params.append("statut", statut);
        const qs = params.toString();
        return `/retrait/${qs ? `?${qs}` : ""}`;
      },
      providesTags: ["retrait"],
    }),
    addRetrait: build.mutation<Retrait, RetraitFormData>({
      query: (data) => ({
        url: `/retrait/`,
        method: "POST",
        body: data,
      }),
      onCacheEntryAdded(arg, { dispatch }) {
        dispatch(
          ProjetApi.util.invalidateTags(["listProjet", "projet", "projet_user"])
        );
      },
      invalidatesTags: ["retrait"],
    }),
    confirmer: build.mutation<Retrait, { slug: string; code: string }>({
      query: ({ slug, code }) => ({
        url: `/retrait/${slug}/confirmer/`,
        method: "POST",
        body: { code },
      }),
      onCacheEntryAdded(arg, { dispatch }) {
        dispatch(
          ProjetApi.util.invalidateTags(["listProjet", "projet", "projet_user"])
        );
      },
      invalidatesTags: ["retrait"],
    }),
    renvoyerCode: build.mutation<{ message: string }, string>({
      query: (slug) => ({
        url: `/retrait/${slug}/renvoyer-code/`,
        method: "POST",
        body: {},
      }),
    }),
    // Bareme applicable a une destination : le formulaire doit afficher le
    // bon minimum et la bonne commission avant la saisie.
    bareme: build.query<Bareme, { mode?: string; pays?: string; canal?: string }>({
      query: ({ mode, pays, canal }) => {
        const p = new URLSearchParams();
        if (mode) p.append("mode", mode);
        if (pays) p.append("pays", pays);
        if (canal) p.append("canal", canal);
        const qs = p.toString();
        return `/bareme-retrait/${qs ? `?${qs}` : ""}`;
      },
      providesTags: ["regle"],
    }),
    regles: build.query<RegleRetrait[], void>({
      query: () => `/regle-retrait/`,
      providesTags: ["regle"],
    }),
    ajouterRegle: build.mutation<RegleRetrait, RegleFormData>({
      query: (body) => ({ url: `/regle-retrait/`, method: "POST", body }),
      invalidatesTags: ["regle"],
    }),
    modifierRegle: build.mutation<RegleRetrait, { slug: string } & RegleFormData>({
      query: ({ slug, ...body }) => ({
        url: `/regle-retrait/${slug}/`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["regle"],
    }),
    supprimerRegle: build.mutation<void, string>({
      query: (slug) => ({ url: `/regle-retrait/${slug}/`, method: "DELETE" }),
      invalidatesTags: ["regle"],
    }),
    // Numero de carte en clair, pour l'admin qui verse. Jamais mis en cache :
    // il est redemande a chaque fois et ne reste pas dans le store.
    numeroCarte: build.query<
      { carte_numero: string; titulaire: string | null; banque: string | null },
      string
    >({
      query: (slug) => `/retrait/${slug}/carte/`,
      keepUnusedDataFor: 0,
    }),
    updateStatut: build.mutation<Retrait, RetraitStatutFormData>({
      query: ({ slug, ...body }) => ({
        url: `/retrait/${slug}/`,
        method: "PUT",
        body,
      }),
      onCacheEntryAdded(arg, { dispatch }) {
        dispatch(
          ProjetApi.util.invalidateTags(["listProjet", "projet", "projet_user"])
        );
      },
      invalidatesTags: ["retrait"],
    }),
  }),
});

export const {
  useBaremeQuery,
  useReglesQuery,
  useAjouterRegleMutation,
  useModifierRegleMutation,
  useSupprimerRegleMutation,
  useRetraitsQuery,
  useAddRetraitMutation,
  useConfirmerMutation,
  useRenvoyerCodeMutation,
  useUpdateStatutMutation,
  useLazyNumeroCarteQuery,
} = RetraitApi;

export default RetraitApi;
